import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { HOME_WRAP } from "./homeTheme";
import Reveal from "./Reveal";

function Counter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const target = parseInt(String(value).replace(/[^\d]/g, ""), 10) || 0;
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setN(target); return undefined; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 1200);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  // Reserve the final width up-front so the count-up never shifts the layout.
  return (
    <span ref={ref} className="relative inline-block tabular-nums">
      <span className="invisible" aria-hidden="true">{target}{suffix}</span>
      <span className="absolute inset-0">{n}{suffix}</span>
    </span>
  );
}

/** A CMS stat like {number:"1", title:"ISO 9001:2015 Certified"} reads better as "ISO 9001:2015 / Certified". Text is only split, never rewritten. */
function shape(item) {
  const iso = String(item.title || "").match(/^(ISO\s*[\d:]+)\s+(.+)$/i);
  if (iso) return { display: iso[1], label: iso[2], counter: false };
  const numeric = /\d/.test(String(item.number ?? "")) && !/[a-z]/i.test(String(item.number));
  return { display: item.number, suffix: item.suffix || "", label: item.title, counter: numeric };
}

/**
 * Horizontal trust bar overlapping the hero's lower edge. White card, hairline
 * dividers, subtle shadow. Data: `data.stats` (Admin → Home Page → Stats).
 * 5-across on desktop; 2-column grid on phones (last item spans the row).
 */
export default function StatsBar({ data }) {
  const stats = (Array.isArray(data?.stats) ? data.stats : []).filter((s) => s.status !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  if (!stats.length) return null;
  return (
    <section className="relative z-10 -mt-20 lg:-mt-24" aria-label="Shingare at a glance">
      <div className={HOME_WRAP}>
        <Reveal y={16}>
          <dl className="grid grid-cols-2 overflow-hidden rounded-2xl border border-[#E4E9F2] bg-white shadow-[0_28px_60px_-30px_rgba(7,27,58,0.45)] lg:grid-cols-5">
            {stats.map((s, i) => {
              const v = shape(s);
              const last = i === stats.length - 1 && stats.length % 2 === 1;
              return (
                <div
                  key={`${s.title}-${i}`}
                  className={`flex flex-col justify-center px-5 py-6 sm:px-8 sm:py-8 lg:px-5 xl:px-8 ${i % 2 === 1 ? "border-l" : ""} ${i >= 2 ? "border-t" : ""} border-[#E4E9F2] lg:border-t-0 ${i > 0 ? "lg:border-l" : "lg:border-l-0"} ${last ? "col-span-2 border-l-0 lg:col-span-1 lg:border-l" : ""}`}
                >
                  <dd className={`font-heading font-semibold leading-none tracking-[-0.02em] text-[#071B3A] ${v.counter ? "text-[30px] sm:text-[36px]" : "whitespace-nowrap text-[22px] sm:text-[24px] lg:text-[18px] xl:text-[24px]"}`}>
                    {v.counter ? <Counter value={v.display} suffix={v.suffix} /> : <span className="tabular-nums">{v.display}{v.suffix}</span>}
                  </dd>
                  <dt className="mt-2 text-[13px] leading-snug text-[#667085]">{v.label}</dt>
                </div>
              );
            })}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}
